import { type ReactNode, useEffect, useId, useRef } from "react";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronRight } from "lucide-react";

import { useBundleSplitterTasks } from "@/hooks/queries/use-bundle-splitter";

import { Badge } from "@components/ui/badge";

import { cn } from "@utils/ui";

import { TaskStatusWatcher } from "./TaskStatusWatcher";
import type { BundleUploadRow } from "./store/useBundleUploads";
import { stageSummary } from "./utils/bundleToastTitle";
import type { CaseRowGroup } from "./utils/groupRowsByCase";
import {
  type PriorUpload,
  type UploadConflict,
  findUploadConflicts,
} from "./utils/uploadConflicts";

type CaseSectionProps = {
  group: CaseRowGroup;
  renderRow: (row: BundleUploadRow, conflict?: UploadConflict) => ReactNode;
};

const isNotStarted = (row: BundleUploadRow) =>
  !row.taskId && (row.status === "ready" || row.status === "failed");

/**
 * MDRS-550: one case's uploads in the bundle pop-up. The header shows the
 * case label and its stage counts (still visible collapsed) and opens or
 * collapses the rows below. Rows picked twice — already listed, or already
 * uploaded per this user's task list for the case — are flagged before they
 * start (the list is the one "Upload case bundle" fetches, so it's cached).
 *
 * Built on the Radix primitives rather than @components/ui/accordion: the
 * header must be an h2 with a right chevron, and collapsed rows must stay
 * mounted (forceMount), none of which the wrapper allows.
 */
export const CaseSection = ({ group, renderRow }: CaseSectionProps) => {
  // Names the section's region — the case label alone, not the counts — and
  // keeps the case number out of the page.
  const labelId = useId();
  const { data: tasksData, refetch: refetchTasks } = useBundleSplitterTasks({
    case_number: group.caseNumber,
  });
  const priorUploads: PriorUpload[] = (tasksData?.tasks ?? []).flatMap((task) => {
    const fileName = task.file_names?.[0];
    return fileName
      ? [
          {
            taskId: task.task_id,
            fileName,
            fileSize: task.file_size ?? null,
            status: task.status,
          },
        ]
      : [];
  });
  const conflicts = findUploadConflicts(group.rows, priorUploads);

  // The cached task list can lag the server (it's fresh for minutes, and
  // another tab's upload never refreshes it): check it again whenever a file
  // is picked — not when one starts or is removed.
  const notStartedCount = group.rows.filter(isNotStarted).length;
  const previousNotStartedCount = useRef(0);
  useEffect(() => {
    if (notStartedCount > previousNotStartedCount.current) {
      void refetchTasks({ cancelRefetch: false });
    }
    previousNotStartedCount.current = notStartedCount;
  }, [notStartedCount, refetchTasks]);

  // Earlier uploads a picked file waits on. The list only learns they ended
  // when their status is polled, and they aren't rows here, so watch them.
  const waitingOnTaskIds = [
    ...new Set(
      [...conflicts.values()].flatMap((conflict) => conflict.waitingOnTaskIds)
    ),
  ];

  return (
    <AccordionPrimitive.Item
      value={group.caseNumber}
      className="rounded-lg border border-border"
    >
      <AccordionPrimitive.Header asChild>
        <h2>
          <AccordionPrimitive.Trigger className="flex w-full items-start gap-2 rounded-lg p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronRight
              aria-hidden
              className={cn(
                "mt-0.5 size-4 shrink-0 text-foreground-muted transition-transform",
                group.isOpen && "rotate-90"
              )}
            />
            <span className="min-w-0 flex-1">
              <span
                id={labelId}
                className="block break-words text-sm font-medium text-foreground"
              >
                {group.caseLabel ?? "Case details unavailable"}
              </span>
              <span className="block text-xs text-foreground-muted">
                {stageSummary(group.rows)}
              </span>
            </span>
            {group.isActive && <Badge variant="secondary">This case</Badge>}
          </AccordionPrimitive.Trigger>
        </h2>
      </AccordionPrimitive.Header>
      {/* forceMount + our own `hidden`: collapsing must only hide the rows —
        their upload, polling and save live in them, so unmounting would
        lose the work (AP-13). */}
      <AccordionPrimitive.Content
        forceMount
        hidden={!group.isOpen}
        aria-labelledby={labelId}
        className="space-y-3 px-3 pb-3"
      >
        {group.rows.map((row) => renderRow(row, conflicts.get(row.id)))}
        {waitingOnTaskIds.map((taskId) => (
          <TaskStatusWatcher key={taskId} taskId={taskId} />
        ))}
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
};
